import {Alert, Button, Col, Container, FormSelect, InputGroup, Row, Spinner} from "react-bootstrap";
import {useEffect, useMemo, useState} from "react";
import ReactDatePicker from "react-datepicker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar} from "@fortawesome/free-solid-svg-icons";
import {
    collection,
    CollectionReference,
    getDocs,
    limit,
    orderBy,
    query,
    QueryConstraint,
    QueryDocumentSnapshot,
    startAfter,
    where,
} from "firebase/firestore";
import {appFirestore} from "../../../../../index";
import RRTable from "../../../../components/rr-table/RRTable";
import {dateTimeToString, dateToString} from "../../../../tools";
import {logisticsDriverChangeLogFBConverter} from "../../LogisticsDriverChangeLogFBConverter";
import {
    LOGISTICS_CHANGE_LOG_COLLECTION,
    LogisticsChangeLogType,
    LogisticsDriverChangeLogType,
} from "../../LogisticsDriverChangeLogType";

const CHANGE_LOG_PAGE_SIZE = 50;

type ChangeLogFilter = "ALL" | LogisticsChangeLogType;

type ChangeLogFilters = {
    startDate: Date;
    endDate: Date;
    changeType: ChangeLogFilter;
};

type ChangeLogPage = {
    changes: LogisticsDriverChangeLogType[];
    lastDocument: QueryDocumentSnapshot<LogisticsDriverChangeLogType> | null;
    hasMore: boolean;
};

function startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);

    return result;
}

function endOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);

    return result;
}

function createDefaultFilters(): ChangeLogFilters {
    const endDate = startOfDay(new Date());
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 29);

    return {startDate, endDate, changeType: "ALL"};
}

function toChangeLogFilter(value: string): ChangeLogFilter {
    switch (value) {
        case "DRIVER":
        case "TRUCK":
        case "PLAN_START_DATE":
        case "PLAN_END_DATE":
        case "ROUTE":
        case "EVENT_NUMBER":
        case "DAY_TYPE":
        case "REPORT_START_DATE":
        case "REPORT_END_DATE":
        case "START_KM":
        case "END_KM":
        case "REPORT_NOTES":
        case "DELETE":
            return value;
        default:
            return "ALL";
    }
}

function changeTypeLabel(changeType: LogisticsChangeLogType): string {
    switch (changeType) {
        case "DRIVER":
            return "Служител";
        case "TRUCK":
            return "Превозно средство";
        case "PLAN_START_DATE":
            return "Планова начална дата / час";
        case "PLAN_END_DATE":
            return "Планова крайна дата / час";
        case "ROUTE":
            return "Маршрут";
        case "EVENT_NUMBER":
            return "Номер на събитие";
        case "DAY_TYPE":
            return "Вид ден";
        case "REPORT_START_DATE":
            return "Начална дата / час";
        case "REPORT_END_DATE":
            return "Крайна дата / час";
        case "START_KM":
            return "Начален километраж";
        case "END_KM":
            return "Краен километраж";
        case "REPORT_NOTES":
            return "Бележки";
        case "DELETE":
            return "Изтриване";
    }
}

function hasVisibleChange(previousValue: string, newValue: string, changeType: LogisticsChangeLogType): boolean {
    // Server emits plan events only for actual changes, including filling/clearing empty fields.
    if (["PLAN_START_DATE", "PLAN_END_DATE", "ROUTE", "EVENT_NUMBER", "DAY_TYPE"].includes(changeType)) {
        return true;
    }
    const normalizedPreviousValue = previousValue.trim();

    return normalizedPreviousValue.length > 0 &&
        normalizedPreviousValue !== "—" &&
        normalizedPreviousValue !== "(празно)" &&
        !normalizedPreviousValue.endsWith(": без избрано превозно средство") &&
        normalizedPreviousValue !== newValue.trim();
}

function dateValue(value: Date | null): string {
    return value ? dateTimeToString(value) : "—";
}

function getQueryConstraints(filters: ChangeLogFilters): QueryConstraint[] {
    const constraints: QueryConstraint[] = [
        where("changedAt", ">=", startOfDay(filters.startDate)),
        where("changedAt", "<=", endOfDay(filters.endDate)),
    ];

    if (filters.changeType !== "ALL") {
        constraints.push(where("changeType", "==", filters.changeType));
    }

    constraints.push(orderBy("changedAt", "desc"));

    return constraints;
}

/** Зарежда до 50 видими записа, без да пропуска документи при филтриране на стари данни. */
async function loadVisiblePage(
    changeLogCollection: CollectionReference<LogisticsDriverChangeLogType>,
    filters: ChangeLogFilters,
    initialCursor: QueryDocumentSnapshot<LogisticsDriverChangeLogType> | null
): Promise<ChangeLogPage> {
    const visibleChanges: LogisticsDriverChangeLogType[] = [];
    let cursor = initialCursor;
    let hasMore = true;

    while (visibleChanges.length < CHANGE_LOG_PAGE_SIZE && hasMore) {
        const remainingRecords = CHANGE_LOG_PAGE_SIZE - visibleChanges.length;
        const pageConstraints = cursor ? [startAfter(cursor)] : [];
        const changeLogSnapshot = await getDocs(query(
            changeLogCollection,
            ...getQueryConstraints(filters),
            ...pageConstraints,
            limit(remainingRecords)
        ));

        visibleChanges.push(...changeLogSnapshot.docs
            .map(change => change.data())
            .filter(change => hasVisibleChange(change.previousValue, change.newValue, change.changeType))
        );
        cursor = changeLogSnapshot.docs[changeLogSnapshot.docs.length - 1] ?? cursor;
        hasMore = changeLogSnapshot.docs.length === remainingRecords;
    }

    return {
        changes: visibleChanges,
        lastDocument: cursor,
        hasMore,
    };
}

export default function LogisticsDriverChangeLogReportView() {
    const initialFilters = useMemo(() => createDefaultFilters(), []);
    const [filters, setFilters] = useState<ChangeLogFilters>(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState<ChangeLogFilters>(initialFilters);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [changes, setChanges] = useState<LogisticsDriverChangeLogType[]>([]);
    const [lastDocument, setLastDocument] = useState<QueryDocumentSnapshot<LogisticsDriverChangeLogType> | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const changeLogCollection = useMemo(() => collection(
        appFirestore,
        LOGISTICS_CHANGE_LOG_COLLECTION
    ).withConverter(logisticsDriverChangeLogFBConverter), []);

    useEffect(() => {
        let active = true;

        async function loadFirstPage(): Promise<void> {
            setLoading(true);
            setErrorMessage(null);
            setChanges([]);
            setLastDocument(null);
            setHasMore(false);

            try {
                const page = await loadVisiblePage(changeLogCollection, appliedFilters, null);

                if (!active) {
                    return;
                }

                setChanges(page.changes);
                setLastDocument(page.lastDocument);
                setHasMore(page.hasMore);
            } catch (error) {
                if (!active) {
                    return;
                }

                setErrorMessage(error instanceof Error ? error.message : "Неуспешно зареждане на лога.");
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }

        void loadFirstPage();

        return () => {
            active = false;
        };
    }, [appliedFilters, changeLogCollection, refreshCounter]);

    async function loadMore(): Promise<void> {
        if (!lastDocument || loadingMore) {
            return;
        }

        setLoadingMore(true);
        setErrorMessage(null);

        try {
            const page = await loadVisiblePage(changeLogCollection, appliedFilters, lastDocument);

            setChanges(currentChanges => [
                ...currentChanges,
                ...page.changes,
            ]);
            setLastDocument(page.lastDocument);
            setHasMore(page.hasMore);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "Неуспешно зареждане на следващата страница.");
        } finally {
            setLoadingMore(false);
        }
    }

    function applyFilters(): void {
        if (filters.startDate > filters.endDate) {
            alert("Началната дата не може да е след крайната дата.");
            return;
        }

        setAppliedFilters({...filters});
        setRefreshCounter(currentCounter => currentCounter + 1);
    }

    return <Container fluid>
        <h1>Лог промени в транспорт</h1>

        <Row className="g-2 mt-3 flex-nowrap">
            <Col>
                <Row className="lh-2-5em flex-nowrap">
                    <Col xs="auto">
                        <Row>
                            <Col xs="auto" className="lh-2-5em">Начална дата:</Col>
                            <Col xs="auto" className="d-inline-block">
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        className="form-control flex-nowrap"
                                        dateFormat="dd.MM.yyyy"
                                        id="driverChangeLogPeriodFromDate"
                                        maxDate={filters.endDate}
                                        onChange={(date: Date | null) => date && setFilters({...filters, startDate: date})}
                                        selected={filters.startDate}
                                        value={dateToString(filters.startDate)}
                                    />
                                    <label htmlFor="driverChangeLogPeriodFromDate">
                                        <InputGroup.Text className="lh-2-5em">
                                            <FontAwesomeIcon icon={faCalendar} size="xl"/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs="auto">
                        <Row>
                            <Col xs="auto" className="lh-2-5em">Крайна дата:</Col>
                            <Col xs="auto" className="d-inline-block">
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        className="form-control flex-nowrap"
                                        dateFormat="dd.MM.yyyy"
                                        id="driverChangeLogPeriodToDate"
                                        minDate={filters.startDate}
                                        onChange={(date: Date | null) => date && setFilters({...filters, endDate: date})}
                                        selected={filters.endDate}
                                        value={dateToString(filters.endDate)}
                                    />
                                    <label htmlFor="driverChangeLogPeriodToDate">
                                        <InputGroup.Text className="lh-2-5em">
                                            <FontAwesomeIcon icon={faCalendar} size="xl"/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs="auto">
                        <Row>
                            <Col xs="auto" className="lh-2-5em">Променено:</Col>
                            <Col xs="auto" className="d-inline-block">
                                <FormSelect
                                    className="mb-2"
                                    onChange={event => setFilters({...filters, changeType: toChangeLogFilter(event.target.value)})}
                                    value={filters.changeType}
                                >
                                    <option value="ALL">Всички</option>
                                    <option value="DRIVER">Служител</option>
                                    <option value="TRUCK">Превозно средство</option>
                                    <option value="PLAN_START_DATE">Планова начална дата / час</option>
                                    <option value="PLAN_END_DATE">Планова крайна дата / час</option>
                                    <option value="ROUTE">Маршрут</option>
                                    <option value="EVENT_NUMBER">Номер на събитие</option>
                                    <option value="DAY_TYPE">Вид ден</option>
                                    <option value="REPORT_START_DATE">Начална дата / час</option>
                                    <option value="REPORT_END_DATE">Крайна дата / час</option>
                                    <option value="START_KM">Начален километраж</option>
                                    <option value="END_KM">Краен километраж</option>
                                    <option value="REPORT_NOTES">Бележки</option>
                                    <option value="DELETE">Изтриване</option>
                                </FormSelect>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <Button onClick={applyFilters}>Покажи</Button>
                    </Col>
                </Row>
            </Col>
        </Row>

        {loading && <Spinner animation="border"/>}
        {errorMessage && <Alert variant="danger">Грешка при зареждане на лога: {errorMessage}</Alert>}
        {!loading && !errorMessage && <>
            <RRTable>
                <thead>
                <tr>
                    <th>Дата на промяната</th>
                    <th>Променено</th>
                    <th>№ събитие</th>
                    <th>Период</th>
                    <th>Преди</th>
                    <th>След</th>
                    <th>Променил</th>
                </tr>
                </thead>
                <tbody>
                {changes.length === 0 && <tr>
                    <td className="text-center" colSpan={7}>Няма промени за избрания период.</td>
                </tr>}
                {changes.map(change => <tr key={change.id}>
                    <td>{dateTimeToString(change.changedAt)}</td>
                    <td>{changeTypeLabel(change.changeType)}</td>
                    <td>{change.eventNumber}</td>
                    <td>{dateValue(change.periodStart)} – {dateValue(change.periodEnd)}</td>
                    <td>{change.previousValue}</td>
                    <td>{change.newValue}</td>
                    <td>{change.changedByName}</td>
                </tr>)}
                </tbody>
            </RRTable>

            <div className="d-flex align-items-center gap-2 mt-3">
                <span>Показани записи: {changes.length}</span>
                {hasMore && <Button disabled={loadingMore} onClick={() => void loadMore()} variant="outline-primary">
                    {loadingMore ? <Spinner animation="border" size="sm"/> : "Зареди още"}
                </Button>}
            </div>
        </>}
    </Container>;
}
